/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  FoodicsAuthorizeCallbackAPI,
  FoodicsStartImportAPI,
} from '../../../api/foodics.api';

import {
  CCard,
  CCardBody,
  CCol,
  CFooter,
  CImage,
  CRow,
  CSpinner,
  CButton,
} from '@coreui/react-pro';

import appStore from '../../../assets/stores/app-store.svg';
import googlePlay from '../../../assets/stores/google-play.svg';
import headerLogo from './daifLogo.svg';
import foodicsLogo from './foodics.png';
import tv from './tv.png';
import './index.scss';
import { CardHeader } from 'react-bootstrap';
import {
  clearFoodicsPriceWithVatSession,
  getFoodicsPriceWithVatSession,
} from './storage';

type StepStatus = 'idle' | 'running' | 'success' | 'error';

const FoodicsCallback = () => {
  const [code, setCode] = useState<string>('');
  const [state, setState] = useState<string>('');
  const [authStatus, setAuthStatus] = useState<StepStatus>('idle');
  const [importStatus, setImportStatus] = useState<StepStatus>('idle');
  const [importDoneStatus, setImportDoneStatus] = useState<StepStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const appStoreLink = useMemo(() => `itms-apps://itunes.apple.com/app/id6450213994`, []);
  const playStoreLink = useMemo(
    () => `market://details?id=com.beautybook.customer`,
    [],
  );

  const { mutate: authorize } = useMutation(FoodicsAuthorizeCallbackAPI, {
    onMutate: () => setAuthStatus('running'),
    onSuccess: (response) => {
      setAuthStatus('success');
      const data = response.data;
      console.log(data, 'data'  );

      const priceWithVat = getFoodicsPriceWithVatSession();
      if (priceWithVat === null) {
        setImportStatus('error');
        setImportDoneStatus('error');
        setErrorMessage(
          'Foodics price mode is missing. Please start the integration again and select a price mode.',
        );
        return;
      }

      startImport({
        providerId: data.providerId,
        priceWithVat,
      });
    },
    onError: (err: any) => {
      setAuthStatus('error');
      setErrorMessage(err?.data?.message || 'Authorization failed.');
    },
  });

  const { mutate: startImport } = useMutation(FoodicsStartImportAPI, {
    onMutate: () => setImportStatus('running'),
    onSuccess: () => {
      setImportStatus('success');
      setImportDoneStatus('success');
      clearFoodicsPriceWithVatSession();
    },
    onError: (err: any) => {
      setImportStatus('error');
      setImportDoneStatus('error');
      setErrorMessage(err?.data?.message || 'Failed to start import.');
    },
  });

  const handleRestart = () => {
    clearFoodicsPriceWithVatSession();
    window.location.href = '/foodics-integration';
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const codeParam = urlParams.get('code') || '';
    const stateParam = urlParams.get('state') || '';
    const errorParam = urlParams.get('error') || '';
    const errorDesc = urlParams.get('error_description') || '';
    setCode(codeParam);
    setState(stateParam);

    if (errorParam) {
      setErrorMessage(errorDesc || errorParam);
      setAuthStatus('error');
      return;
    }

    if (!codeParam || !stateParam) {
      setErrorMessage('Missing code/state from Foodics redirect.');
      setAuthStatus('error');
      return;
    }

    authorize({ code: codeParam, state: stateParam });
  }, [authorize]);

  const renderStatus = (label: string, status: StepStatus) => {
    const color =
      status === 'success' ? '#28a745' : status === 'error' ? '#dc3545' : '#ddddddff';

    return (
      <CRow className="justify-content-center mb-3" style={{ color }}>
        <CCol xs="12" lg="6" className="text-center">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center' }}>
            {status === 'running' ? (
              <CSpinner size="sm" color="secondary" />
            ) : status === 'success' ? (
              <span style={{ color }} >✓</span>
            ) : status === 'error' ? (
              <span style={{ color }}>✕</span>
            ) : (
              <span style={{ color }}>•</span>
            )}
            <span style={{ color }}>{label}</span>
          </div>
        </CCol>
      </CRow>
    );
  };

  return (
    <div
      style={{
        backgroundImage: `url(${tv})`,
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <title>Daif - Foodics Integration</title>
      <CCard
        style={{
          backgroundImage: `url(${tv})`,
          minHeight: '80dvh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
         <CardHeader className="booking-header">
          <div className="booking-logo">
            <CImage
              width={'100px'}
              height={'50px'}
              align="start"
              alt="logo"
              src={headerLogo}
              className="profile-picture"
            />
            <span style={{ marginLeft: '20px', marginRight: '20px' }}>|</span>
            <CImage
              width={'120px'}
              height={'50px'}
              align="start"
              alt="logo"
              src={foodicsLogo}
              className="profile-picture"
            />
          </div>
        </CardHeader>
        <CCardBody style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center',flex:1 }}>
          <CRow className="justify-content-center mb-4">
            <CCol xs="12" lg="8" className="text-center">
              <h5 style={{ color: '#fff' }}>Finalizing Foodics Integration</h5>
              <p style={{ color: '#fff', opacity: 0.85 }}>
                Please keep this page open while we verify authorization and start importing your
                data.
              </p>
            </CCol>
          </CRow>

          {renderStatus('Validating authorization with Foodics', authStatus)}
          {renderStatus('Starting data import', importStatus)}
          {renderStatus('Import completed', importDoneStatus)}

          {errorMessage && (
            <CRow className="justify-content-center mt-3">
              <CCol xs="12" lg="6" className="text-center">
                <div style={{ color: '#ffdddd', background: '#b3000022', padding: 12, borderRadius: 8 }}>
                  {errorMessage}
                </div>
                {authStatus === 'error' && (
                  <div style={{ color: '#fff', opacity: 0.9, marginTop: 10 }}>
                    Authorization could not be validated. Please start again on the Foodics platform and approve the integration.
                  </div>
                )}
                <div style={{ marginTop: 16 }}>
                  <CButton color="secondary" onClick={handleRestart}>
                    Start again on Foodics
                  </CButton>
                </div>
              </CCol>
            </CRow>
          )}

          {authStatus === 'success' && importDoneStatus === 'success' && (
            <CRow className="justify-content-center mt-3">
              <CCol xs="12" lg="6" className="text-center" style={{ color: '#d4edda' }}>
                <h6>Congratulations !</h6>
                <p>Import finished successfully. You can safely close this page.</p>
              </CCol>
            </CRow>
          )}
        </CCardBody>
      </CCard>

      <CFooter className="booking-footer">
        <div style={{ width: '100%' }}>
          <CRow className="justify-content-center align-items-center pb-5 mt-3">
            <CCol xs="auto">
              <a href={appStoreLink} className="text-white mr-4">
                <img src={appStore} alt="appStore" style={{ maxWidth: '150px', maxHeight: '50px' }} />
              </a>
            </CCol>
            <CCol xs="auto">
              <a href={playStoreLink} className="text-white">
                <img src={googlePlay} alt="googlePlay" style={{ maxWidth: '150px', maxHeight: '50px' }} />
              </a>
            </CCol>
          </CRow>
        </div>
      </CFooter>
    </div>
  );
};

export default FoodicsCallback;
